import { useState, useEffect } from "react";
import { getExercises } from "@/lib/storage";
import { ExerciseImage } from "./ExerciseImage";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Loader2, Search, Trash2, UserCircle } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Switch } from "@/components/ui/switch";
import { DeleteExerciseDialog } from "./DeleteExerciseDialog";
import { ExerciseDetailsDialog } from "./ExerciseDetailsDialog";
import { useTranslations } from "@/hooks/useTranslations";

interface CustomExercise {
  id: string | number;
  name: string;
  body_part: string;
  equipment?: string | null;
  target_muscle?: string | null;
  image_url?: string | null;
  difficulty?: string | null;
  rating?: number | null;
  instructions?: string[];
  isCustom?: boolean;
  trainer_id?: string;
  youtube_url?: string | null;
  created_at?: string;
  [key: string]: any;
}

interface ExerciseLibraryProps {
  onSelectExercise?: (exercise: any) => void;
  canSelect?: boolean;
  showCustomExercises?: boolean;
}

export const ExerciseLibrary = ({
  onSelectExercise,
  canSelect = false,
  showCustomExercises = false
}: ExerciseLibraryProps) => {
  const [exercises, setExercises] = useState<CustomExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [bodyParts, setBodyParts] = useState<string[]>([]);
  const [equipments, setEquipments] = useState<string[]>([]);
  const [targetMuscles, setTargetMuscles] = useState<string[]>([]);

  const [selectedBodyPart, setSelectedBodyPart] = useState<string>("");
  const [selectedEquipment, setSelectedEquipment] = useState<string>("");
  const [selectedTargetMuscle, setSelectedTargetMuscle] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedExercise, setSelectedExercise] = useState<CustomExercise | null>(null);

  const { language } = useLanguage();
  const { user } = useAuth();
  const { t } = useTranslations("dashboard-exercise-library");
  
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [deleteExercise, setDeleteExercise] = useState<{id: string, name: string} | null>(null);

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        setLoading(true);

        const filters = {
          bodyPart: selectedBodyPart && selectedBodyPart !== "_all" ? selectedBodyPart : undefined,
          equipment: selectedEquipment && selectedEquipment !== "_all" ? selectedEquipment : undefined,
          targetMuscle: selectedTargetMuscle && selectedTargetMuscle !== "_all" ? selectedTargetMuscle : undefined,
          searchTerm: searchTerm || undefined
        };

        let data = await getExercises(language, filters) as CustomExercise[];

        if (showCustomExercises && user?.id) {
          const { data: custom, error } = await supabase
            .from("custom_exercises")
            .select("*")
            .or(`trainer_id.eq.${user.id}${user.role === "trainer" ? "" : ""}`);
          
          if (custom) {
            if (showCustomOnly) {
              data = custom.map(ex => ({
                ...ex,
                isCustom: true,
                image_url: ex.image_url || null,
              }));
            } else {
              data = [
                ...data,
                ...custom.map(ex => ({
                  ...ex,
                  isCustom: true,
                  image_url: ex.image_url || null,
                }))
              ];
            }
          }
        }

        setExercises(data);

        if (!selectedBodyPart && !selectedEquipment && !selectedTargetMuscle && !searchTerm) {
          const uniqueBodyParts = [...new Set(data.map((ex: any) => ex.body_part))].filter(Boolean) as string[];
          const uniqueEquipments = [...new Set(data.map((ex: any) => ex.equipment))].filter(Boolean) as string[];
          const uniqueTargetMuscles = [...new Set(data.map((ex: any) => ex.target_muscle))].filter(Boolean) as string[];

          setBodyParts(uniqueBodyParts.sort());
          setEquipments(uniqueEquipments.sort());
          setTargetMuscles(uniqueTargetMuscles.sort());
        }

      } catch (error) {
        console.error("Erro ao carregar exercícios:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, [selectedBodyPart, selectedEquipment, selectedTargetMuscle, searchTerm, showCustomExercises, user?.id, showCustomOnly, refreshKey, language]);

  const handleResetFilters = () => {
    setSelectedBodyPart("");
    setSelectedEquipment("");
    setSelectedTargetMuscle("");
    setSearchTerm("");
  };

  const totalPages = Math.ceil(exercises.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExercises = exercises.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        {showCustomExercises && (
          <div className="flex items-center gap-2">
            <Switch
              id="custom-only"
              checked={showCustomOnly}
              onCheckedChange={setShowCustomOnly}
            />
            <label
              htmlFor="custom-only"
              className="text-sm text-muted-foreground dark:text-gray-300 whitespace-nowrap flex items-center gap-1"
            >
              <UserCircle className="h-4 w-4" />
              {t("showCustomOnly")}
            </label>
          </div>
        )}
        <Button
          variant="outline"
          onClick={handleResetFilters}
          className="ml-auto whitespace-nowrap dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
        >
          {t("resetFilters")}
        </Button>
      </div>

      <div>
        <div className="text-sm font-medium text-muted-foreground mb-1 dark:text-gray-300">{t("filters")}</div>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-grow w-full sm:w-1/3">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground dark:text-gray-400" />
            <Input
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 bg-white border border-slate-100 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-2 w-full sm:w-2/3">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground dark:text-gray-400">{t("bodyPart")}</div>
              <Select
                value={selectedBodyPart}
                onValueChange={setSelectedBodyPart}
              >
                <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
                  <SelectValue placeholder={t("bodyPart")} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
                  <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allBodyParts")}</SelectItem>
                  {bodyParts.map(part => (
                    <SelectItem key={part} value={part} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{part}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <div className="text-xs text-muted-foreground dark:text-gray-400">{t("equipment")}</div>
              <Select
                value={selectedEquipment}
                onValueChange={setSelectedEquipment}
              >
                <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
                  <SelectValue placeholder={t("equipment")} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
                  <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allEquipment")}</SelectItem>
                  {equipments.map(equip => (
                    <SelectItem key={equip} value={equip} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{equip}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <div className="text-xs text-muted-foreground dark:text-gray-400">{t("targetMuscle")}</div>
              <Select
                value={selectedTargetMuscle}
                onValueChange={setSelectedTargetMuscle}
              >
                <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
                  <SelectValue placeholder={t("targetMuscle")} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
                  <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allMuscles")}</SelectItem>
                  {targetMuscles.map(muscle => (
                    <SelectItem key={muscle} value={muscle} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{muscle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground dark:text-gray-400" />
        </div>
      ) : !paginatedExercises.length ? (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground dark:text-gray-300">{t("noExercisesFound")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {paginatedExercises.map((exercise) => (
              <Card 
                key={`${exercise.id}${exercise.isCustom ? "_custom" : ""}`} 
                className={`overflow-hidden border border-slate-100/50 hover:shadow-md transition-all dark:bg-gray-800 dark:border-gray-700 cursor-pointer`}
                onClick={() => {
                  if (canSelect) {
                    onSelectExercise?.(exercise);
                  } else {
                    setSelectedExercise(exercise);
                  }
                }}
              >
                <div className="p-4">
                  <ExerciseImage
                    imagePath={exercise.image_url || exercise.gif_url}
                    altText={exercise.name}
                    fallbackText={exercise.name}
                    className="w-full h-auto"
                    isCustomExercise={'isCustom' in exercise && exercise.isCustom}
                  />
                </div>
                <CardContent className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium line-clamp-2 dark:text-white flex items-center gap-1">
                      {exercise.name}
                      {exercise.isCustom && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-flame/10 border border-flame/30 rounded text-flame">
                          {t("custom.badge")}
                        </span>
                      )}
                    </h3>
                    {exercise.isCustom && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteExercise({ id: exercise.id.toString(), name: exercise.name });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="text-xs text-muted-foreground dark:text-gray-300 space-y-1">
                    <p><span className="font-medium dark:text-gray-200">{t("bodyPart")}:</span> {exercise.body_part}</p>
                    {exercise.equipment && (
                      <p><span className="font-medium dark:text-gray-200">{t("equipment")}:</span> {exercise.equipment}</p>
                    )}
                    {exercise.target_muscle && (
                      <p><span className="font-medium dark:text-gray-200">{t("targetMuscle")}:</span> {exercise.target_muscle}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {totalPages > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) setCurrentPage(currentPage - 1);
                    }}
                    className={`${currentPage === 1 ? "pointer-events-none opacity-50" : ""} dark:text-white dark:hover:bg-gray-700`}
                  />
                </PaginationItem>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageToShow = currentPage;
                  if (currentPage < 3) {
                    pageToShow = i + 1;
                  } else if (currentPage > totalPages - 2) {
                    pageToShow = totalPages - 4 + i;
                  } else {
                    pageToShow = currentPage - 2 + i;
                  }
                  if (pageToShow > 0 && pageToShow <= totalPages) {
                    return (
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(pageToShow);
                          }}
                          isActive={currentPage === pageToShow}
                          className="dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600"
                        >
                          {pageToShow}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                  return null;
                })}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                    }}
                    className={`${currentPage === totalPages ? "pointer-events-none opacity-50" : ""} dark:text-white dark:hover:bg-gray-700`}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
          
          <DeleteExerciseDialog
            open={!!deleteExercise}
            onOpenChange={(open) => !open && setDeleteExercise(null)}
            exerciseId={deleteExercise?.id || ''}
            exerciseName={deleteExercise?.name || ''}
            onDeleted={() => {
              setRefreshKey(prev => prev + 1);
            }}
          />
          
          <ExerciseDetailsDialog
            open={!!selectedExercise}
            onOpenChange={(open) => !open && setSelectedExercise(null)}
            exercise={selectedExercise}
          />
        </>
      )}
    </div>
  );
};
